import React, { useState } from 'react';
import { 
  Zap, 
  DollarSign, 
  Brain, 
  Image, 
  FileText, 
  Code, 
  BarChart3,
  Check,
  X,
  Info,
  ArrowRight,
  Sparkles,
  Copy,
  Play,
  Key,
  AlertCircle
} from 'lucide-react';
import { ModelPlatform, PromptComparison } from '../types/prompt';
import { modelCapabilities } from '../data/multiModelTemplates';
import { useApiKeys } from '../contexts/ApiKeyContext';
import aiService from '../services/aiService';

interface ModelComparisonProps {
  onSelectModels?: (models: ModelPlatform[]) => void;
}

const ModelComparison: React.FC<ModelComparisonProps> = ({ onSelectModels }) => {
  const { getApiKeyForModel, isKeyConfigured } = useApiKeys();
  const [selectedModels, setSelectedModels] = useState<Set<ModelPlatform>>(new Set());
  const [comparisonMode, setComparisonMode] = useState<'features' | 'cost' | 'performance'>('features');
  const [testPrompt, setTestPrompt] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<PromptComparison | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Check API key availability for each model
  const getModelApiStatus = (model: ModelPlatform) => {
    switch(model) {
      case 'gpt4':
      case 'gpt4-vision':
      case 'dalle3':
        return !!getApiKeyForModel('gpt4');
      case 'claude':
        return !!getApiKeyForModel('claude');
      case 'gemini':
        return !!getApiKeyForModel('gemini');
      case 'stable-diffusion-xl':
        return !!getApiKeyForModel('stability');
      case 'llama':
        return !!getApiKeyForModel('meta');
      case 'mistral':
        return !!getApiKeyForModel('mistral');
      default:
        return false;
    }
  };

  const models: ModelPlatform[] = [
    'claude', 'gpt4', 'gpt4-vision', 'gemini', 
    'dalle3', 'stable-diffusion-xl', 'llama', 'mistral'
  ];

  const modelInfo = {
    'claude': { 
      name: 'Claude 3 Opus', 
      icon: '🤖', 
      type: 'text',
      provider: 'Anthropic'
    },
    'gpt4': { 
      name: 'GPT-4 Turbo', 
      icon: '🧠', 
      type: 'text',
      provider: 'OpenAI'
    },
    'gpt4-vision': { 
      name: 'GPT-4 Vision', 
      icon: '👁️', 
      type: 'multimodal',
      provider: 'OpenAI'
    },
    'gemini': { 
      name: 'Gemini Pro', 
      icon: '✨', 
      type: 'multimodal',
      provider: 'Google'
    },
    'dalle3': { 
      name: 'DALL-E 3', 
      icon: '🎨', 
      type: 'image',
      provider: 'OpenAI'
    },
    'stable-diffusion-xl': { 
      name: 'SDXL', 
      icon: '🖼️', 
      type: 'image',
      provider: 'Stability AI'
    },
    'llama': { 
      name: 'LLaMA 2', 
      icon: '🦙', 
      type: 'text',
      provider: 'Meta'
    },
    'mistral': { 
      name: 'Mistral', 
      icon: '🌊', 
      type: 'text',
      provider: 'Mistral AI'
    }
  };

  const toggleModel = (model: ModelPlatform) => {
    const newSelected = new Set(selectedModels);
    if (newSelected.has(model)) {
      newSelected.delete(model);
    } else {
      newSelected.add(model);
    }
    setSelectedModels(newSelected);
    if (onSelectModels) {
      onSelectModels(Array.from(newSelected));
    }
  };

  const calculateCost = (model: ModelPlatform, tokens: number = 1000): number => {
    const capabilities = modelCapabilities[model];
    if ('costPerToken' in capabilities) {
      return capabilities.costPerToken * tokens;
    } else if ('costPerImage' in capabilities) {
      return capabilities.costPerImage;
    }
    return 0;
  };

  const runComparison = () => {
    if (selectedModels.size === 0 || !testPrompt) return;

    const comparison: PromptComparison = {
      models: Array.from(selectedModels),
      prompts: new Map(),
      metrics: {
        cost: new Map(),
        speed: new Map(),
        quality: new Map()
      }
    };

    selectedModels.forEach(model => {
      comparison.prompts.set(model, testPrompt);
      comparison.metrics!.cost.set(model, calculateCost(model));
      comparison.metrics!.speed.set(model, Math.random() * 5 + 1);
      comparison.metrics!.quality.set(model, Math.random() * 20 + 80);
    });

    setComparisonResults(comparison);
    setShowResults(true);
  };

  const getModelScore = (model: ModelPlatform): number => {
    if (!comparisonResults?.metrics) return 0;
    const cost = comparisonResults.metrics.cost.get(model) || 0;
    const speed = comparisonResults.metrics.speed.get(model) || 0;
    const quality = comparisonResults.metrics.quality.get(model) || 0;
    
    return (quality * 0.5) + ((1 / (cost + 0.001)) * 0.3) + ((1 / speed) * 0.2);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 text-white rounded-xl p-6 mb-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <BarChart3 className="w-10 h-10" />
          <h1 className="text-3xl font-bold">Model Comparison Tool</h1>
        </div>
        <p className="text-lg opacity-90">
          Compare different AI models side-by-side to find the best fit for your needs
        </p>
      </div>

      {/* Mode Selector */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setComparisonMode('features')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              comparisonMode === 'features'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Brain className="w-4 h-4 inline mr-2" />
            Features
          </button>
          <button
            onClick={() => setComparisonMode('cost')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              comparisonMode === 'cost'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Cost Analysis
          </button>
          <button
            onClick={() => setComparisonMode('performance')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              comparisonMode === 'performance'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Performance
          </button>
        </div>
      </div>

      {/* Model Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {models.map(model => {
          const info = modelInfo[model as string] || { name: model, icon: '❓', type: 'unknown', provider: 'Unknown' };
          const capabilities = modelCapabilities[model as keyof typeof modelCapabilities] || {};
          const isSelected = selectedModels.has(model);
          
          return (
            <div
              key={model}
              onClick={() => toggleModel(model)}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-lg transform scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md bg-white'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="text-3xl">{info.icon}</div>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isSelected ? 'bg-blue-500' : 'bg-gray-200'
                }`}>
                  {isSelected && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
              
              <h3 className="font-semibold text-gray-900 mb-1">{info.name}</h3>
              <p className="text-xs text-gray-500 mb-2">{info.provider}</p>
              
              {/* API Key Status */}
              {getModelApiStatus(model) ? (
                <div className="flex items-center gap-1 mb-2">
                  <Key className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600 font-medium">API Ready</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 mb-2">
                  <AlertCircle className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">No API Key</span>
                </div>
              )}
              
              {/* Model Type Badge */}
              <div className="flex gap-1 mb-3">
                {info.type === 'text' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    <FileText className="w-3 h-3 inline mr-1" />
                    Text
                  </span>
                )}
                {info.type === 'image' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    <Image className="w-3 h-3 inline mr-1" />
                    Image
                  </span>
                )}
                {info.type === 'multimodal' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Multi
                  </span>
                )}
              </div>

              {/* Quick Stats based on mode */}
              {comparisonMode === 'features' && (
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    <strong>Best for:</strong>
                  </p>
                  <ul className="text-xs text-gray-500 space-y-0.5">
                    {(capabilities.bestFor || []).slice(0, 2).map((use: string, idx: number) => (
                      <li key={idx} className="truncate">• {use}</li>
                    ))}
                  </ul>
                </div>
              )}

              {comparisonMode === 'cost' && (
                <div className="space-y-1">
                  {'costPerToken' in capabilities && (
                    <p className="text-sm font-medium text-gray-700">
                      ${(capabilities.costPerToken * 1000).toFixed(3)}/1K tokens
                    </p>
                  )}
                  {'costPerImage' in capabilities && (
                    <p className="text-sm font-medium text-gray-700">
                      ${capabilities.costPerImage.toFixed(3)}/image
                    </p>
                  )}
                </div>
              )}

              {comparisonMode === 'performance' && (
                <div className="space-y-1">
                  {'maxTokens' in capabilities && (
                    <p className="text-xs text-gray-600">
                      <strong>Max:</strong> {(capabilities.maxTokens / 1000).toFixed(0)}K tokens
                    </p>
                  )}
                  <p className="text-xs text-gray-600">
                    <strong>Speed:</strong> {'⚡'.repeat(Math.min(5, Math.floor(Math.random() * 5 + 1)))}
                  </p>
                </div>
              )}
              
              {/* API Key Status */}
              {isKeyConfigured && (
                <div className="mt-2 p-2 rounded text-xs">
                  {isKeyConfigured(model) ? (
                    <div className="flex items-center gap-1 text-green-600">
                      <Key className="w-3 h-3" />
                      <span>API key configured</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-500">
                      <AlertCircle className="w-3 h-3" />
                      <span>No API key</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Test Prompt Section */}
      {selectedModels.size > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Test Prompt Comparison
          </h3>
          
          <textarea
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            placeholder="Enter a test prompt to compare how different models would handle it..."
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
          />
          
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Selected models: {selectedModels.size}
            </p>
            <button
              onClick={runComparison}
              disabled={!testPrompt || selectedModels.size === 0}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Run Comparison
            </button>
          </div>
        </div>
      )}

      {/* Comparison Results */}
      {showResults && comparisonResults && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Comparison Results</h3>
          
          <div className="space-y-4">
            {Array.from(selectedModels).map(model => {
              const info = modelInfo[model as string] || { name: model, icon: '❓', type: 'unknown', provider: 'Unknown' };
              const score = getModelScore(model);
              const cost = comparisonResults.metrics?.cost.get(model) || 0;
              const speed = comparisonResults.metrics?.speed.get(model) || 0;
              const quality = comparisonResults.metrics?.quality.get(model) || 0;
              
              return (
                <div key={model} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{info.icon}</span>
                      <div>
                        <h4 className="font-semibold">{info.name}</h4>
                        <p className="text-xs text-gray-500">{info.provider}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">
                        Score: {score.toFixed(1)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div className="text-center p-2 bg-green-50 rounded">
                      <DollarSign className="w-4 h-4 text-green-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Cost</p>
                      <p className="text-sm font-semibold">${cost.toFixed(4)}</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <Zap className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Speed</p>
                      <p className="text-sm font-semibold">{speed.toFixed(1)}s</p>
                    </div>
                    <div className="text-center p-2 bg-purple-50 rounded">
                      <Brain className="w-4 h-4 text-purple-600 mx-auto mb-1" />
                      <p className="text-xs text-gray-600">Quality</p>
                      <p className="text-sm font-semibold">{quality.toFixed(0)}%</p>
                    </div>
                  </div>
                  
                  {/* Recommendation */}
                  {score === Math.max(...Array.from(selectedModels).map(getModelScore)) && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-yellow-600" />
                      <span className="text-xs text-yellow-800 font-medium">
                        Recommended for this use case
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feature Comparison Table */}
      {selectedModels.size > 1 && comparisonMode === 'features' && (
        <div className="bg-white rounded-xl shadow-lg p-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-4">Feature Comparison</h3>
          
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-700">Feature</th>
                {Array.from(selectedModels).map(model => (
                  <th key={model} className="text-center py-2 px-3 text-sm font-medium text-gray-700">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-lg">{modelInfo[model as string]?.icon || '❓'}</span>
                      <span className="text-xs">{modelInfo[model as string]?.name || model}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">Text Generation</td>
                {Array.from(selectedModels).map(model => (
                  <td key={model} className="text-center py-3 px-3">
                    {['text', 'multimodal'].includes(modelInfo[model as string]?.type || '') ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">Image Generation</td>
                {Array.from(selectedModels).map(model => (
                  <td key={model} className="text-center py-3 px-3">
                    {['image'].includes(modelInfo[model as string]?.type || '') ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">Vision/Image Input</td>
                {Array.from(selectedModels).map(model => (
                  <td key={model} className="text-center py-3 px-3">
                    {['multimodal', 'gpt4-vision'].includes(model) ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr className="border-b hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">Code Generation</td>
                {Array.from(selectedModels).map(model => {
                  const info = modelInfo[model] || { name: model, icon: '❓', type: 'unknown', provider: 'Unknown' };
                  return (
                  <td key={model} className="text-center py-3 px-3">
                    {['text', 'multimodal'].includes(info.type) ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 mx-auto" />
                    )}
                  </td>
                  );
                })}
              </tr>
              <tr className="border-b hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">Open Source</td>
                {Array.from(selectedModels).map(model => (
                  <td key={model} className="text-center py-3 px-3">
                    {['llama', 'stable-diffusion-xl', 'mistral'].includes(model) ? (
                      <Check className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <X className="w-4 h-4 text-gray-300 mx-auto" />
                    )}
                  </td>
                ))}
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-3 px-3 text-sm text-gray-600">API Available</td>
                {Array.from(selectedModels).map(model => (
                  <td key={model} className="text-center py-3 px-3">
                    <Check className="w-4 h-4 text-green-500 mx-auto" />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Tips */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200 mt-6">
        <h3 className="text-lg font-semibold mb-3 text-blue-900 flex items-center gap-2">
          <Info className="w-5 h-5" />
          Model Selection Tips
        </h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>Claude:</strong> Best for nuanced analysis, creative writing, and complex reasoning tasks</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>GPT-4:</strong> Excellent general-purpose model with strong math and coding capabilities</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>Gemini:</strong> Great for research tasks with extremely long context windows</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>DALL-E 3:</strong> Premium image generation with excellent text rendering</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span><strong>SDXL:</strong> Cost-effective image generation with customization options</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ModelComparison;